/** Sidebar — 1:1 port z chrome.jsx.
 *  width 232px, padding 20/14, bg-card, border-right.
 *  Active item: gold-12 bg + gold-hi text + gold icon. */

import { NavLink, useLocation } from "react-router";
import { Wordmark } from "@/components/ui/wordmark";
import { Ic, type IconKey } from "@/components/ui/icons";
import { t } from "@/i18n/de";

interface NavItem {
  to: string;
  label: string;
  icon: IconKey;
  liveBadge?: boolean;
  soonBadge?: boolean;
}

const NAV_ITEMS: NavItem[] = [
  { to: "/", label: t.nav.dashboard, icon: "Dashboard" },
  { to: "/this-week", label: t.nav.thisWeek, icon: "Calendar" },
  { to: "/next-week", label: t.nav.nextWeek, icon: "CalendarNext" },
  { to: "/orders", label: t.nav.orders, icon: "Stack" },
  { to: "/invoices", label: t.nav.invoices, icon: "Receipt" },
  { to: "/balance", label: t.nav.balance, icon: "Scale" },
  { to: "/map", label: t.nav.map, icon: "Map", liveBadge: true },
  { to: "/shop", label: t.nav.shop, icon: "Shop", soonBadge: true },
];

export function Sidebar() {
  const location = useLocation();
  const isActive = (path: string) =>
    path === "/" ? location.pathname === "/" : location.pathname.startsWith(path);

  return (
    <aside
      style={{
        width: 232,
        flex: "0 0 232px",
        background: "var(--bg-card)",
        borderRight: "1px solid var(--border)",
        display: "flex",
        flexDirection: "column",
        padding: "20px 14px",
        height: "100%",
      }}
    >
      <div style={{ padding: "4px 8px 24px" }}>
        <Wordmark size={24} />
      </div>

      <nav style={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {NAV_ITEMS.map((item) => {
          const active = isActive(item.to);
          const Icon = Ic[item.icon];
          return (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === "/"}
              className="nav-item"
              data-active={active ? "true" : "false"}
            >
              <span className="nav-icon">
                <Icon size={16} />
              </span>
              <span style={{ flex: 1, textAlign: "left" }}>{item.label}</span>
              {item.liveBadge && (
                <span
                  style={{
                    fontSize: 9,
                    fontWeight: 600,
                    letterSpacing: "0.08em",
                    color: "var(--gold)",
                    textTransform: "uppercase",
                  }}
                >
                  {t.nav.live}
                </span>
              )}
              {item.soonBadge && (
                <span
                  style={{
                    fontSize: 9,
                    fontWeight: 600,
                    letterSpacing: "0.08em",
                    color: "var(--text-3)",
                    textTransform: "uppercase",
                    border: "1px solid var(--border)",
                    borderRadius: 999,
                    padding: "2px 6px",
                    background: "var(--bg-base-2)",
                  }}
                >
                  {t.nav.soon}
                </span>
              )}
            </NavLink>
          );
        })}
      </nav>

      <div style={{ flex: 1 }} />

      <NavLink
        to="/account"
        className="nav-item"
        data-active={location.pathname === "/account" ? "true" : "false"}
      >
        <span className="nav-icon">
          <Ic.User size={16} />
        </span>
        <span>{t.nav.profile}</span>
      </NavLink>

      <div
        style={{
          marginTop: 12,
          padding: "12px 10px",
          borderRadius: "var(--r-md)",
          background: "var(--bg-base-2)",
          border: "1px solid var(--border)",
          fontSize: 12,
          color: "var(--text-2)",
        }}
      >
        <div
          style={{
            color: "var(--text-3)",
            fontSize: 10.5,
            textTransform: "uppercase",
            letterSpacing: "0.08em",
            marginBottom: 4,
          }}
        >
          {t.nav.abrechnungUeber}
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <span style={{ color: "var(--text)", fontWeight: 500 }}>Warmteo DE</span>
          <Ic.ChevronDown size={12} />
        </div>
      </div>

      <style>{`
        .nav-item {
          display: flex; align-items: center; gap: 10px;
          height: 36px; padding: 0 10px;
          border-radius: 8px;
          background: transparent; border: 0;
          color: var(--text-2);
          font-size: 13.5px; font-weight: 500;
          transition: background .12s, color .12s;
          width: 100%;
          text-decoration: none;
        }
        .nav-item:hover { background: var(--bg-base-2); color: var(--text); }
        .nav-item[data-active="true"] {
          background: var(--gold-12);
          color: var(--gold-hi);
        }
        .nav-item[data-active="true"] .nav-icon { color: var(--gold); }
        .nav-icon { display: inline-flex; width: 18px; align-items: center; justify-content: center; }
      `}</style>
    </aside>
  );
}
